import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();

    this.playTextScroll();
    window.addEventListener('resize', () => {
      this.playTextScroll();
    });

    this.closeButton = this.parentElement.querySelector('.svg-wrapper.close');
    this.closeButton?.addEventListener('click', () => {
      this.closeAnnouncementBar();
    });
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  playTextScroll() {
    const slides = this.querySelectorAll('.announcement-bar__slide');

    slides.forEach((slide) => {
      const wrapper = slide.querySelector('.announcement-bar__text-wrapper');
      const track = slide.querySelector('.announcement-bar__text-track');
      const text = slide.querySelector('.announcement-bar__text');

      if (!wrapper || !track || !text) return;

      // Remove previous clone
      const clone = track.querySelector('[aria-hidden="true"]');
      if (clone) clone.remove();

      wrapper.classList.remove('is-overflowing');
      track.classList.remove('is-scrolling');

      // Check if this slide's text overflows
      if (text.scrollWidth <= wrapper.clientWidth) return;

      wrapper.classList.add('is-overflowing');

      const gap = 100;
      const distance = text.offsetWidth + gap;
      const duration = distance / 50;

      // Clone this slide's text
      const newClone = text.cloneNode(true);
      newClone.setAttribute('aria-hidden', 'true');

      track.appendChild(newClone);

      track.style.setProperty('--scroll-distance', `${distance}px`);
      track.style.setProperty('--scroll-duration', `${duration}s`);

      track.classList.add('is-scrolling');
    });
  }

  closeAnnouncementBar() {
    this.parentElement.style.display = 'none';

    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    const defaultTop = isMobile ? 0 : 20;

    document.body.style.setProperty('--header-offset', `${defaultTop}px`);
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
